import type { CameraPosition, GenerateImageOptions, GenerateImageRendererOptions, GeneratedImage, ModelViewerGenerateImageCallback } from "@centricsoftware/model-viewer";
import { ModelFace } from "@centricsoftware/model-viewer";
import { CircularProgress } from "@mui/material";
import { TYPE_MATERIALDRAPE } from "csi/model/document";
import { getComponentItemName } from "csi/page/localeProvider";
import { GenerateImageFormat, SnapshotMimeType } from "csi/threeD/snapshotTemplates/snapshotUtils";
import { C8Node, cnl } from "csi/typings";
import { sortByString, spliceBy } from "csi/utility/arrayUtility";
import { getFileNameWithoutExtension } from "csi/utility/fileUtility";
import { ReactionStore } from "csi/utility/mobxUtility";
import ErrorDialog from "designSystem/components/dialog/ErrorDialog";
import MessageDialog from "designSystem/components/dialog/MessageDialog";
import { Button } from "designSystem/index";
import { ILocaleProvider } from "designSystem/locale";
import { ComponentItem } from "designSystem/types/component-item-types";
import { sortBy } from "lodash";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import React from "react";
import { rightPaneStore } from "../rootStore";
import CanvasStore from "./canvasStore";
import EnvironmentsStore from "./environmentsStore";
import MaterialSettingsStore from "./materialSettingsStore";
import { uploadSnapshots } from "./snapshotsService";
import { SnapshotSaveLocationData } from "./threeDViewerStore";

type SnapshotConfiguration = {
	cameraPosition?: CameraPosition;
	modelFace?: ModelFace;
	variant?: string;
};

type ProcessedImageData = {
	image: string;
	file: File;
};
export interface SnapshotDataMap {
    img: string;
    file: File;
	imgGallerySrc?: string[];
	isSelected: boolean;
	isVisible: boolean;
	isGenerated?: boolean;
	isTurntable?: boolean;
	configuration?: SnapshotConfiguration;
	turntableFiles?: File[];
}

class Snapshot {
    @observable isSelected: boolean;
    @observable isVisible: boolean;
    file: File;
    imgGallerySrc?: string[];
    img: string;
    isGenerated?: boolean;
    isTurntable?: boolean;
    configuration?: SnapshotConfiguration;
    turntableFiles?: File[];
    constructor(data: SnapshotDataMap) {
    	this.img = data.img;
    	this.file = data.file;
    	this.imgGallerySrc = data.imgGallerySrc;
    	this.isSelected = data.isSelected;
    	this.isVisible = data.isVisible;
    	this.isGenerated = data.isGenerated;
    	this.isTurntable = data.isTurntable;
    	this.configuration = data.configuration;
    	this.turntableFiles = data.turntableFiles;
    	makeObservable(this);
    }
}

export default class SnapshotsStore extends ReactionStore {
	/** Abort Controller placeholder */
	private abortController: AbortController;

	/** Stores canvas store */
	readonly canvasStore: CanvasStore;
	
	/** Stores environment store */
	readonly environmentStore: EnvironmentsStore;

	/** locale provider to be used in stores */
	readonly localeProvider: ILocaleProvider = null;
	
	/** Stores material settings store */
	readonly materialSettingsStore: MaterialSettingsStore;	
	
	/** Stores 3D title */
	readonly modelName: string;
	
	/** Stores document revision */
	readonly revisionNode: C8Node;

	/** Determine if the GenerateImages page is open */
	@observable generateImagesPageOpen: boolean = false;
	
	/** Stores whether the images are grouped by generation type */
	@observable groupByGenerationType: boolean = true;

	/** Determine if the image tags should show */
	@observable hideTags: boolean = true;

	/** Determine if the images are in the process of being generated */
	@observable isLoadingImages: boolean = false;

	/** Determine is the user has canceled the generate images operation */
	@observable operationHasAborted = false;

	/** Stores the available locations a snapshot can be saved to */
	@observable.ref snapshotSaveLocationsData: SnapshotSaveLocationData | null = null;

	/** Selected snapshot save locations */
	@observable.ref selectedSnapshotSaveLocations: ComponentItem[] = [];

	/** Determine if the snapshot filters are open */
	@observable snapshotFiltersOpen: boolean = false;
	
	/** Stores snapshot data */
	private readonly snapshotData: Snapshot[] = observable.array<Snapshot>([], { deep: false });

	/** Stores the selected camera positions to filter images */
	@observable.ref selectedCameraPositionsFilter: ComponentItem[] = [];

	/** Stores the selected variants to generate images */
	@observable.ref selectedVariants: ComponentItem[] = [];

	/** Selected material sides to be used to generate images */
	@observable.ref selectedMaterialSides: ComponentItem[] = [];

	/** Selected material primitives to be used to generate images */
	@observable.ref selectedMaterialPrimitives: ComponentItem[] = [];

	/** extension point for operations after uploading the snapshots */
	onUploadSnapshot: (snapshotData: Snapshot[]) => void;

	/** Entry point for ModelViewer number of gen images */
	@observable modelViewerNumberOfGenerateImages: (options: GenerateImageOptions) => number;

	/** Entry point for ModelViewer generate images */
	@observable modelViewerGenerateImages: ModelViewerGenerateImageCallback;

	/** return all visible images */
	@computed get allVisibleSnapshots(): Snapshot[] {
		return this.snapshotsData.filter((item) => item.isVisible);
	}

	/** Determine if there are any selected images that the user can delete */
	@computed get canDeleteImages(): boolean {
		return this.snapshotsData.some((item) => item.isSelected);
	}

	/** Determine if all visible images are selected */
	@computed get allVisibleItemSelected(): boolean {
		const { allVisibleSnapshots } = this;
		return allVisibleSnapshots.length > 0 && allVisibleSnapshots.every((item) => item.isSelected);
	}

	/** Determine if the user can save images */
	@computed get canSaveImages(): boolean {
		return !!this.selectedSnapshots?.length && !!this.selectedSnapshotSaveLocations?.length;
	}

	/** Calculate the number of images that will be generated */
	numberOfGenerateImages = (imageOptions: GenerateImageOptions): number => {
		if (!imageOptions) {
			return 0;
		}
		return this.modelViewerNumberOfGenerateImages(imageOptions);
	};

	/** Selected snapshot save locations urls */
	@computed get selectedSnapshotSaveLocationsUrls(): cnl[] {
		return this.selectedSnapshotSaveLocations?.map((item) => item.id);
	}

	/** Material side options */
	get materialSideOptions(): ComponentItem[] {
		const { hasBackSide } = this.materialSettingsStore;
		if (!hasBackSide) {
			return [];
		}
		return [
			{ id: "front", res: "Front" },
			{ id: "back", res: "Back" },
		];
	};

	/** Force all snapshots to be visible */
	@action forceAllSnapshotsVisible = (): void => {
		this.snapshotData.forEach((item) => {
			item.isVisible = true;
		});
	};

	/** Select all visible images */
	@action onSelectAll= (event: React.ChangeEvent, val: boolean): void => {
		this.setSelectedSnapshots(val, this.allVisibleSnapshots);
	};

	/** Set flag to determine if the user has canceled the generate images operation */
	@action setOperationHasAborted = (value: boolean): void => {
		this.operationHasAborted = value;
	};
	
	/** External handler to abort generate and upload of images */
	@action abortImageGeneration = ():void => {
		this.setOperationHasAborted(true);
		this.abortController?.abort();
	};
	
	/** Generate Images */
	@action generateImages = async (imageOptions: GenerateImageOptions, rendererOptions: GenerateImageRendererOptions): Promise<void> => {
		const { modelViewerGenerateImages } = this;
		
	    // Initialize a new AbortController
		this.abortController = new AbortController();
		const abortSignal = this.abortController.signal;

		let onAbort: () => void;

		const abortPromise = new Promise<never>((_, reject) => {
			onAbort = (): void => {
				reject();
			};
			abortSignal.addEventListener("abort", onAbort);
		});
		
		// Things we need to parse from the options we pass into the generateImages function
		const imageFormat = rendererOptions.imageType === SnapshotMimeType.JPEG ? GenerateImageFormat.JPEG : GenerateImageFormat.PNG;
		const imageTypeIsTurntable = imageOptions?.camera?.turntable != null;
		const numberOfGeneratedImages = this.numberOfGenerateImages(imageOptions);

		const format = imageFormat.toLowerCase();

		rightPaneStore.openDialog(MessageDialog, {
			category: null,
			messageRes: "3DGeneratingNumImages",
			messageResParams: [numberOfGeneratedImages],
			titleRes: "3DGeneratingImages",
			children: {
				icon: React.createElement(CircularProgress, { size: 20 }),
				buttons: React.createElement(Button, {
					design: "link",
					onClick: this.abortImageGeneration,
					res: "Cancel",
				}),
			},
		});

		await Promise.race([
			await modelViewerGenerateImages(imageOptions, rendererOptions, this.abortController.signal),
			abortPromise, 
		])
			.then(action((allUniqueImages: GeneratedImage[][]) => {
				const fileName = this.modelName || "generatedImage";
			
				const processImages = (images: GeneratedImage[]): ProcessedImageData[] => {
					return images.map((image, index) => {
						const snapshotName = `${index + 1}.${format}`;
						const file = new File([image.blob], snapshotName, { type: image.blob.type });
						return { image: image.image, file };
					});
				};
			
				const createSnapshot = (image: string, file: File, imgGallerySrc = [], turntableFiles = []): SnapshotDataMap => ({
					img: image,
					file,
					isSelected: false,
					isGenerated: true,
					imgGallerySrc,
					turntableFiles,
					isTurntable: imageTypeIsTurntable,
					isVisible: true,
				});
			
				allUniqueImages.forEach((images) => {
					if (imageTypeIsTurntable) {
						const results = processImages(images);
						const oneTurnTableImage = {
							...createSnapshot(results[0].image, results[0].file, results.map((item) => item.image), results.map((item) => item.file)),
							configuration: {
								variant: images[0].configuration?.variant,
							},
						};
						this.setSnapshot(oneTurnTableImage);
					} else {
						images.forEach((image) => {
							const snapshotName = `${getFileNameWithoutExtension(fileName)}_${Date.now()}.${format}`;
							const file = new File([image.blob], snapshotName, { type: image.blob.type });
							const snapshotData = {
								...createSnapshot(image.image, file),
								configuration: {
									cameraPosition: image.configuration?.cameraPosition,
									modelFace: image.configuration?.modelFace,
									variant: image.configuration?.variant,
								},
							};
							this.setSnapshot(snapshotData);
						});
					}
				});
				rightPaneStore.closeDialog();
				this.toggleGenerateImagesPage();
				this.clearGenerateImageOptions();

				if (this.operationHasAborted){
					rightPaneStore.openDialog(ErrorDialog, {
						messageRes: "3DViewerGenerateImagesAbortMessage",
						titleRes: "3DGenerateImagesAbortTitle",
						onClose: () => {
							this.setOperationHasAborted(false);
						},
					});
				}
			}))
			.catch((error)=>{
				rightPaneStore.openDialog(ErrorDialog, {
					messageRes: "3DGenerateImagesError",
					titleRes: "3DGeneratingImages",
				});
			})
			.finally(() => {
				this.abortController = null;
				abortSignal.removeEventListener("abort", onAbort);
			});
	};

	/** returns snapshot data */
	get snapshotsData(): Snapshot[] {
    	return this.snapshotData;
	}

	/** returns the generated images in snapshotsData */
	get generatedImages(): Snapshot[] {
		return this.snapshotData.filter((item) => item.isGenerated && item.isVisible && !item.isTurntable);
	}

	/** returns the generated images that are turntables */
	get turntableImages(): Snapshot[] {
		return this.snapshotData.filter((item) => item.isGenerated && item.isVisible && item.isTurntable);
	}

	/** returns the user images in snapshotsData */
	get userImages(): Snapshot[] {
		return this.snapshotData.filter((item) => !item.isGenerated && item.isVisible);
	}

	/** returns the selected snapshots */
	get selectedSnapshots(): Snapshot[] {
		return this.snapshotData.filter((item) => item.isSelected);
	}

	/** set a snapshot as selected */
	@action setSelectedSnapshots = (value: boolean, data:Snapshot[]): void => {
		const snapshots = this.snapshotsData.filter((item) => data.includes(item));
		snapshots.forEach((item) => {
			item.isSelected = value;
		});
	};

	/** Determine if snapshots are allowed - (i.e snapshots are not allowed when viewing a material drape (physics) file ) */
	get allowSnapshots(): boolean {
    	return this.revisionNode.$Type !== TYPE_MATERIALDRAPE && this.saveLocations.length > 0;
	}

	/** Determine if there are options selected so that the user can generate images */
	canGenerateImages = (imageOptions: GenerateImageOptions, rendererOptions: GenerateImageRendererOptions): boolean => {
		if (!imageOptions || !rendererOptions) {
			return false;
		}
		const { selectedVariants } = this;
		const { imageRatio, environment, imageType } = rendererOptions;
	
		const isTurntable = imageOptions.camera.turntable !== undefined;
		// if the image type is image the user must select a camera position
		const hasSelectedCameraPositions = isTurntable ? true : imageOptions.camera.cameraPositions?.length > 0;
		// if the model has no variants, the user can still generate images
		const hasSelectedVariants = this.variantOptions.length ? !!selectedVariants?.length : true;
		// if the image type is turntable the user must select an image count
		const hasSelectedImageCount = isTurntable ? !!imageOptions.camera.turntable?.numberOfImages : true;
		
		const hasSelectedEnvironment = !!environment;
		const hasSelectedImageRatio = !!imageRatio;
		const hasSelectedImageFormat = !!imageType;

		// if the image type is a material then ensure that the user has selected a material side
		const hasMaterialSides = this.materialSettingsStore.isU3MFabric ? !!imageOptions.materials.sides?.length : true;

		// if the material primitive selector is shown, ensure that the user has selected a primitive
		const hasMaterialPrimitives = this.materialSettingsStore.showPrimitiveSelector ? !!imageOptions.materials.primitives?.length : true;
		
		return hasSelectedCameraPositions && hasSelectedVariants && hasSelectedEnvironment && hasSelectedImageCount && hasSelectedImageRatio && hasSelectedImageFormat && hasMaterialSides && hasMaterialPrimitives;
	};

	/** All possible variant options to generate an image */
	get variantOptions(): ComponentItem[] {
		const { variants } = this.canvasStore;
		if (!variants?.length) {
			return [];
		}
		return variants;
	}

	/** All possible camera positions filters */
	@computed get cameraPositionsFilterOptions(): ComponentItem[] {
		const { snapshotData } = this;
		const cameraPositions = snapshotData
			.filter((item) => item.configuration?.cameraPosition)
			.map((item) => item.configuration.cameraPosition);

		const uniqueCameraPositions = Array.from(new Set(cameraPositions.map((item) => item.id)))
			.map((id) => cameraPositions.find((item) => item.id === id));
		
		sortByString(uniqueCameraPositions, "name");

		return uniqueCameraPositions.map((item) => ({
			id: item.id,
			name: item.name,
		}));
	}

	/** toggle the hideTags property */
	@action toggleHideTags = (): void => {
		this.hideTags = !this.hideTags;
	};

	/** Determine whether or not to show "No Result Found" placeholder */
	@computed get showNoResultsFound(): boolean {
		return !this.snapshotsData?.some((item) => item.isVisible); 
	}

	/** toggle GenerateImages page */
	@action toggleGenerateImagesPage = (): void => {
		this.generateImagesPageOpen = !this.generateImagesPageOpen;
	};

	/** toggle Snapshot filters */
	@action toggleSnapshotFilters = (): void => {
		this.snapshotFiltersOpen = !this.snapshotFiltersOpen;
	};

    /** Upload snapshot from 3dViewer */
    @action uploadNewSnapshots = async (): Promise<void> => {
    	const { selectedSnapshots, selectedSnapshotSaveLocationsUrls } = this;
    	const titleRes = "3DSavingSnapshots";
    	const urlNames = this.selectedSnapshotSaveLocations?.map((item) => getComponentItemName(item));
    	try {
    		rightPaneStore.openDialog(MessageDialog, {
    			category: "progress",
    			messageRes: "3DSavingSnapshotsMsg",
    			messageResParams: [selectedSnapshots.length, urlNames.join(", ") ],
    			titleRes,
    		});
    		await uploadSnapshots(selectedSnapshots, selectedSnapshotSaveLocationsUrls);
    		runInAction(() => {
    			this.selectedSnapshotSaveLocations = [];
    			this.selectedCameraPositionsFilter = [];				
    			rightPaneStore.closeDialog();
    			rightPaneStore.openDialog(MessageDialog, {
    				category: "success",
    				messageRes: "3DSavingSnapshotsSuccess",
    				messageResParams: [selectedSnapshots.length, urlNames.join(", ") ],
    				titleRes: "3DSnapshotsSaved",
    				onClose: () => {
    					this.deleteSnapshots();
    					this.forceAllSnapshotsVisible();
    				},
    			});
    			this.onUploadSnapshot(selectedSnapshots);
    		});
    	} catch (error) {
    		rightPaneStore.openDialog(ErrorDialog, {
    			messageRes: "RequestErrorMessageAndPotentialReasons",
    			titleRes,
    		});
    	}
    };

    /** stores snapshot image in to snapShotData */
    @action setSnapshot = (data: SnapshotDataMap): void => {
    	this.snapshotData.push(new Snapshot(data));
    };

	/** Set selected material sides */
	@action setSelectedMaterialSides = (event: React.ChangeEvent, materialSides: ComponentItem[]): void => {
		this.selectedMaterialSides = materialSides;
	};

	/** Set selected material primitives */
	@action setSelectedMaterialPrimitives = (event: React.ChangeEvent, materialPrimitives: ComponentItem[]): void => {
		this.selectedMaterialPrimitives = materialPrimitives;
	};

	/** get selected filters based off of the available snapshotData */
	@computed get validatedSelectedFilters(): ComponentItem[] {
		const { snapshotData, selectedCameraPositionsFilter } = this;
		const updatedSelectedCameraPositionsFilter = selectedCameraPositionsFilter.filter((item) => snapshotData.some((snapshot) => snapshot.configuration?.cameraPosition?.id === item.id));
		return updatedSelectedCameraPositionsFilter;
	};

	/** set selected camera positions for filtering images */
	@action setSelectedCameraPositionsFilter = (event: React.ChangeEvent, cameraPositions: ComponentItem[]): void => {
		this.selectedCameraPositionsFilter = cameraPositions;
		const cameraPositionsIds = cameraPositions.map((item) => item.id);
		this.snapshotData.forEach((item) => {
			item.isVisible = cameraPositionsIds.includes(item.configuration?.cameraPosition?.id);
		});

		// if no camera positions are selected, show all snapshots
		if (!cameraPositionsIds.length) {
			this.forceAllSnapshotsVisible();
		}
	};

	/** set group by generation type */
	@action setGroupByGenerationType = (enabled: boolean): void => {
		this.groupByGenerationType = enabled;
	};

	/** set selected variants */
	@action setSelectedVariants = (event: React.ChangeEvent, variants: ComponentItem[]): void => {
		this.selectedVariants = variants;
	};

	/** Remove Snapshots if selected is true */
	@action deleteSnapshots = (): void => {
		spliceBy(this.snapshotData, (item) => item.isSelected);
	};
	
	/** Clear the generate Image options */
	@action clearGenerateImageOptions = (): void => {
		this.selectedVariants = [];
		this.selectedMaterialSides = [];
		this.selectedMaterialPrimitives = [];
	};

	/** Function to return all snapshots for a certain variant type */
	getVariantsFromSnapshots = (variant: string): SnapshotDataMap[] => {
    	return this.snapshotsData.filter((snapshot) => snapshot.configuration?.variant === variant && snapshot.isVisible);
	};

	/** Sets the user selected locations to save snapshots to */
	@action setSelectedSnapshotSaveLocations = (event: React.ChangeEvent, locations: ComponentItem[]): void => {
		this.selectedSnapshotSaveLocations = locations;
	};

	/** Return what group(s) should be suggested to the user */
	get suggestedSaveLocations(): string[] {
		return this.snapshotSaveLocationsData.suggestedLocations;
	};

	/** Return the save locations */
	@computed get saveLocations(): ComponentItem[] {
    	const { snapshotSaveLocationsData: { locations }, localeProvider } = this;
    	if (!locations?.length) {
    		return [];
    	}
    	const saveLocations = locations.map((location) => {
			let group = localeProvider.type(location.$Type);
			let order = "0";
			switch (location.$Type) {
				case "DocumentRevision":
					order = "2";
					break;
				case "Colorway":
					order = "1";
					group = localeProvider.type("Colorway", true);
					break;
				case "ColorMaterial":
					order = "1";
					group = localeProvider.type("ColorMaterial", true);
					break;
				default:
					break;
			}

    		return {
    			id: location.$URL,
    			res: location.$Type === "DocumentRevision" ? "3DCurrentRevision" : "",
				name: location.$Type === "DocumentRevision" ? null : location.$Name,
				group,
				order,				
    		};
    	});
		
		return sortBy(saveLocations, ["order", "name"]);
	};

	/** Function to be used in MUI Multi select for camera positions filters. This ensures that filter options are only compared to the avaliable options */
	compareValuesOnDynamicFilterOptions = (option: ComponentItem, value: ComponentItem): boolean => {	
		if (!this.cameraPositionsFilterOptions.some((opt) => opt.id === value.id)) {
			return true;
		}
		return option.id === value.id;
	};

	constructor(canvasStore:CanvasStore, materialSettingsStore:MaterialSettingsStore, environmentStore:EnvironmentsStore, revisionNode: C8Node, onUploadSnapshot: VoidFunction, snapshotSaveLocations: SnapshotSaveLocationData, threeDTitle: string, locale: ILocaleProvider) {
    	super();
    	makeObservable(this);

    	this.canvasStore = canvasStore;
		this.materialSettingsStore = materialSettingsStore;
		this.environmentStore = environmentStore;
    	this.revisionNode = revisionNode;
    	this.onUploadSnapshot = onUploadSnapshot;
    	this.modelName = threeDTitle;
		this.snapshotSaveLocationsData = snapshotSaveLocations;
		this.localeProvider = locale;

		this.trackReaction(reaction(
			() => this.validatedSelectedFilters, 
			(selectedFilters) => {
				if (!selectedFilters.length) {
					this.forceAllSnapshotsVisible();
				}
			},
		));

		this.trackReaction(reaction(
			() => this.generateImagesPageOpen,
			(generateImagePageIsOpen) => {
				if (generateImagePageIsOpen) {
					this.setSelectedCameraPositionsFilter(null, []);
				}
			},
		));
	}
}